import type { CompanyPanelMenu } from "@/components/company/company-preview-panel-content";
import PanelActionsBar from "@/components/company/shared/panel-actions-bar";
import { ConnectedCrmLogos } from "@/components/integrations/connected-crm-logos";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useConnectedCrmIntegrations } from "@/hooks/useConnectedCrmIntegrations";
import { useTranslation } from "@/hooks/useTranslation";
import { saveCompanyTag } from "@/lib/company-tag-creation";
import type {
  CompanyDTO,
  CompanyFromTagSkipReason,
  CompanyTag,
} from "@shaarp/common";
import { ListIcon, SearchIcon, UserRoundPlusIcon } from "lucide-react";
import { useState, type ReactNode } from "react";

/** What a click on the actions bar of a company not saved yet asked for. */
export type CompanyTagAction = CompanyPanelMenu | "page";

/** What the person is told when the company could not be saved, by reason. */
const SKIP_REASON_MESSAGES: Record<CompanyFromTagSkipReason, string> = {
  homonyms: "company.tagPreview.create.homonyms",
  unavailable: "company.tagPreview.create.unavailable",
  rate_limited: "company.tagPreview.create.rate_limited",
};

/**
 * The actions bar of a company panel — lists, CRM, full page, assignee — under
 * a company the base does not hold yet. The first click on any of them saves
 * the company (`saveCompanyTag`), then hands it over with the action asked
 * for; nothing is saved by merely opening the panel (`docs/decisions/0016`).
 *
 * The full page opens in a tab opened by the click itself, pointed at the
 * company once saved: a tab opened after the request would be blocked.
 */
export default function CompanyTagActionsFooter({
  tag,
  onSaved,
}: {
  tag: CompanyTag;
  onSaved: (company: CompanyDTO, action: CompanyTagAction) => void;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const toastError = useToastError();
  const connectedCrmIntegrations = useConnectedCrmIntegrations();
  const [pending, setPending] = useState<CompanyTagAction | null>(null);

  const saveThenRun = async (action: CompanyTagAction) => {
    const tab = action === "page" ? window.open("", "_blank") : null;
    setPending(action);
    try {
      const save = await saveCompanyTag(api, tag);
      if (save.status === "skipped") {
        tab?.close();
        toastError({
          description: t(SKIP_REASON_MESSAGES[save.reason]),
        });
        return;
      }
      if (tab) tab.location.href = `/company/${save.company.id}`;
      onSaved(save.company, action);
    } catch {
      tab?.close();
      toastError();
    } finally {
      setPending(null);
    }
  };

  // The clicked button spins until the save answers; all are disabled meanwhile.
  const iconOf = (action: CompanyTagAction, idle: ReactNode) =>
    pending === action ? <Spinner /> : idle;
  const isPending = pending !== null;

  return (
    <PanelActionsBar>
      <Button
        variant="outline"
        className="h-10 w-10"
        aria-label={t("company.listings.button")}
        disabled={isPending}
        onClick={() => saveThenRun("listings")}
      >
        {iconOf("listings", <ListIcon className="size-4 shrink-0" />)}
      </Button>
      {connectedCrmIntegrations.length > 0 && (
        <Button
          variant="outline"
          className="h-10"
          aria-label={t("company.integrations.menuTitle")}
          disabled={isPending}
          onClick={() => saveThenRun("crm")}
        >
          {iconOf(
            "crm",
            <ConnectedCrmLogos integrations={connectedCrmIntegrations} />,
          )}
        </Button>
      )}
      <Button
        className="h-10 flex-1 gap-1.5"
        disabled={isPending}
        onClick={() => saveThenRun("page")}
      >
        {iconOf("page", <SearchIcon className="size-4" />)}
        {t("components.dataTableCompanies.columns.sidePanelViewFullPage")}
      </Button>
      <Button
        variant="outline"
        className="size-10 rounded-full"
        aria-label={t("company.userAssignedSelector.assignCompany")}
        disabled={isPending}
        onClick={() => saveThenRun("assignee")}
      >
        {iconOf(
          "assignee",
          <UserRoundPlusIcon className="size-5 text-muted-foreground" />,
        )}
      </Button>
    </PanelActionsBar>
  );
}
